import { z } from "zod";

const jsonObjectSchema = z.record(z.string(), z.unknown());
const dateTimeSchema = z.string().datetime({ offset: true });
export const experienceStrategyDomainIdSchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{1,159}$/u);
export const experienceStrategyOpaqueIdSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,239}$/u);
const revisionSchema = z.string().regex(/^(?:0|[1-9][0-9]*)$/u);
export const experienceStrategyPositiveRevisionSchema = z
  .string()
  .regex(/^[1-9][0-9]*$/u);
export const experienceStrategyDomainHashSchema = z
  .string()
  .regex(/^sha256:[a-f0-9]{64}$/u);

const domainIdSchema = experienceStrategyDomainIdSchema;
const opaqueIdSchema = experienceStrategyOpaqueIdSchema;
const positiveRevisionSchema = experienceStrategyPositiveRevisionSchema;
const proposalHashSchema = experienceStrategyDomainHashSchema;

export const experienceStrategyDimensionKeys = [
  "channel",
  "audience",
  "budget",
  "product",
  "content",
  "promotion",
] as const;

export const experienceStrategyDimensionLabels = {
  audience: "人群定位",
  budget: "预算节奏",
  channel: "渠道打法",
  content: "内容策略",
  product: "产品方向",
  promotion: "促销节奏",
} as const satisfies Record<ExperienceStrategyDimensionKey, string>;

export const experienceStrategyDimensionKeySchema = z.enum(
  experienceStrategyDimensionKeys,
);

const experienceStrategyScopeWireSchema = z
  .object({
    scope_id: z.string().max(160),
    shop_binding_id: z.string().max(160),
    shop_display_name: z.string().max(500),
    shop_provider: z.string().max(80),
    type: z.enum(["organization", "shop"]),
  })
  .strict();

export const experienceStrategyDimensionDraftWireSchema = z
  .object({
    details: jsonObjectSchema,
    headline: z.string().trim().min(1).max(160),
    key: experienceStrategyDimensionKeySchema,
    summary: z.string().trim().max(2_000),
  })
  .strict();

const canonicalExperienceStrategyDimensionsWireSchema = z
  .array(experienceStrategyDimensionDraftWireSchema)
  .min(1)
  .max(experienceStrategyDimensionKeys.length)
  .superRefine((dimensions, context) => {
    let previousIndex = -1;
    const seenKeys = new Set<ExperienceStrategyDimensionKey>();
    dimensions.forEach((dimension, index) => {
      const canonicalIndex = experienceStrategyDimensionKeys.indexOf(
        dimension.key,
      );
      if (seenKeys.has(dimension.key)) {
        context.addIssue({
          code: "custom",
          message: "经验策略维度不能重复",
          path: [index, "key"],
        });
      }
      if (canonicalIndex <= previousIndex) {
        context.addIssue({
          code: "custom",
          message: "经验策略维度必须使用 v1 固定顺序",
          path: [index, "key"],
        });
      }
      seenKeys.add(dimension.key);
      previousIndex = canonicalIndex;
    });
  });

const experienceStrategyMetricWireSchema = z
  .object({
    label: z.string().trim().min(1).max(160),
    value: z.string().trim().max(500),
  })
  .strict();

export const experienceStrategyDraftWireSchema = z
  .object({
    background: z.string().trim().max(4_000),
    core_decisions: z.array(z.string().trim().min(1).max(2_000)).max(50),
    dimensions: canonicalExperienceStrategyDimensionsWireSchema,
    execution_plan: z.array(z.string().trim().min(1).max(2_000)).max(50),
    expected_metrics: z.array(experienceStrategyMetricWireSchema).max(50),
    summary: z.string().trim().max(2_000),
    tags: z.array(z.string().trim().min(1).max(80)).max(32),
    title: z.string().trim().min(1).max(160),
  })
  .strict();

const unavailableExperienceStrategySourceWireSchema = z
  .object({
    available: z.literal(false),
    confirmation_message_id: z.never().optional(),
    confirmation_sequence: z.never().optional(),
    conversation_id: z.never().optional(),
    proposal_message_id: z.never().optional(),
    proposal_sequence: z.never().optional(),
    stale: z.boolean().optional(),
    turn_id: z.never().optional(),
    turn_start_sequence: z.never().optional(),
  })
  .strict();

const availableExperienceStrategySourceWireSchema = z
  .object({
    available: z.literal(true),
    confirmation_message_id: opaqueIdSchema.optional(),
    confirmation_sequence: positiveRevisionSchema.optional(),
    conversation_id: opaqueIdSchema,
    proposal_message_id: opaqueIdSchema,
    proposal_sequence: positiveRevisionSchema,
    stale: z.boolean().optional(),
    turn_id: opaqueIdSchema,
    turn_start_sequence: positiveRevisionSchema,
  })
  .strict();

const experienceStrategySourceWireSchema = z
  .discriminatedUnion("available", [
    unavailableExperienceStrategySourceWireSchema,
    availableExperienceStrategySourceWireSchema,
  ])
  .superRefine((source, context) => {
    if (
      source.available &&
      Boolean(source.confirmation_message_id) !==
        Boolean(source.confirmation_sequence)
    ) {
      context.addIssue({
        code: "custom",
        message: "自然语言确认消息与 sequence 必须成对出现",
        path: ["confirmation_message_id"],
      });
    }
  });

export const experienceStrategyVersionWireSchema = z
  .object({
    background: z.string(),
    confirmation_channel: z.enum(["button", "natural_language"]),
    confirmed_at: dateTimeSchema,
    confirmed_by_user_id: z.string().min(1).optional(),
    core_decisions: z.array(z.string()),
    current_version_id: domainIdSchema,
    dimensions: canonicalExperienceStrategyDimensionsWireSchema,
    execution_plan: z.array(z.string()),
    expected_metrics: z.array(experienceStrategyMetricWireSchema),
    id: domainIdSchema,
    is_current: z.boolean(),
    resolved_timezone: z.string().min(1),
    scope_revision: positiveRevisionSchema,
    shop_binding_id: z.string().optional(),
    shop_display_name: z.string().optional(),
    shop_provider: z.string().optional(),
    source: experienceStrategySourceWireSchema,
    strategy_id: domainIdSchema,
    strategy_revision: positiveRevisionSchema,
    summary: z.string(),
    supersedes_version_id: z.string().optional(),
    tags: z.array(z.string()),
    title: z.string().min(1),
    version: positiveRevisionSchema,
  })
  .strict()
  .superRefine((strategy, context) => {
    if (strategy.is_current !== (strategy.id === strategy.current_version_id)) {
      context.addIssue({
        code: "custom",
        message: "当前版本标记必须与策略 current_version_id 一致",
        path: ["is_current"],
      });
    }
    const hasConfirmationAnchor = Boolean(
      strategy.source.available &&
      strategy.source.confirmation_message_id &&
      strategy.source.confirmation_sequence,
    );
    if (strategy.confirmation_channel === "button" && hasConfirmationAnchor) {
      context.addIssue({
        code: "custom",
        message: "按钮确认不能携带自然语言确认锚点",
        path: ["source", "confirmation_message_id"],
      });
    }
    if (
      strategy.confirmation_channel === "natural_language" &&
      strategy.source.available &&
      !hasConfirmationAnchor
    ) {
      context.addIssue({
        code: "custom",
        message: "自然语言确认的可用来源必须携带确认锚点",
        path: ["source", "confirmation_message_id"],
      });
    }
  });

const timelineDateRangeWireSchema = z
  .object({
    confirmed_before: z.string(),
    confirmed_from: z.string(),
    date_before: z.string(),
    date_from: z.string(),
    shortcut: z.string(),
  })
  .strict();

export const experienceStrategyTimelineResponseSchema = z
  .object({
    items: z.array(experienceStrategyVersionWireSchema),
    next_cursor: z.string(),
    organization_id: domainIdSchema,
    resolved_date_range: timelineDateRangeWireSchema,
    resolved_timezone: z.string().min(1),
    schema_version: z.literal(1),
    scope: experienceStrategyScopeWireSchema,
  })
  .strict();

const currentDimensionUnavailableWireSchema = z
  .object({
    available: z.literal(false),
    current_version_id: z.literal(""),
    details: jsonObjectSchema,
    headline: z.literal(""),
    key: experienceStrategyDimensionKeySchema,
    summary: z.literal(""),
  })
  .strict();

const currentDimensionAvailableWireSchema = z
  .object({
    available: z.literal(true),
    confirmed_at: dateTimeSchema,
    details: jsonObjectSchema,
    headline: z.string().min(1),
    key: experienceStrategyDimensionKeySchema,
    strategy_id: domainIdSchema,
    strategy_revision: positiveRevisionSchema,
    summary: z.string(),
    // version_id 只表示维度来源；调整 CAS 必须使用 current_version_id。
    current_version_id: domainIdSchema,
    version: positiveRevisionSchema,
    version_id: domainIdSchema,
  })
  .strict();

const currentDimensionsWireSchema = z
  .array(
    z.discriminatedUnion("available", [
      currentDimensionUnavailableWireSchema,
      currentDimensionAvailableWireSchema,
    ]),
  )
  .length(experienceStrategyDimensionKeys.length)
  .superRefine((dimensions, context) => {
    dimensions.forEach((dimension, index) => {
      if (dimension.key !== experienceStrategyDimensionKeys[index]) {
        context.addIssue({
          code: "custom",
          message: "当前策略必须使用 v1 固定六维顺序",
          path: [index, "key"],
        });
      }
    });
  });

export const experienceStrategyCurrentResponseSchema = z
  .object({
    dimensions: currentDimensionsWireSchema,
    last_updated_at: z.union([dateTimeSchema, z.literal("")]),
    organization_id: domainIdSchema,
    resolved_timezone: z.string().min(1),
    schema_version: z.literal(1),
    scope: experienceStrategyScopeWireSchema,
    scope_revision: revisionSchema,
    summary: z.string(),
  })
  .strict();

export const experienceStrategyDetailResponseSchema = z
  .object({
    schema_version: z.literal(1),
    strategy: experienceStrategyVersionWireSchema,
  })
  .strict();

const proposalIdentityShape = {
  action: z.enum(["confirm", "adjust"]),
  base_version_id: z.string().max(160),
  expected_scope_revision: revisionSchema,
  expected_strategy_revision: z.string().max(30),
  id: domainIdSchema,
  organization_id: domainIdSchema,
  proposal_hash: proposalHashSchema,
  revision: positiveRevisionSchema,
  scope_id: domainIdSchema,
  status: z.enum([
    "drafted",
    "awaiting_confirmation",
    "confirmed",
    "rejected",
    "stale",
  ]),
  strategy_id: z.string().max(160),
  terminal_reason_code: z.string().max(120),
};

const experienceStrategyProposalBindingWireSchema = z
  .object({
    assistant_message_content_hash: z.string().regex(/^[a-f0-9]{64}$/u),
    assistant_message_id: z.string().min(1),
    assistant_message_sequence: positiveRevisionSchema,
    turn_id: z.string().min(1),
    turn_start_sequence: positiveRevisionSchema,
  })
  .strict();

export const experienceStrategyProposalWireSchema = z
  .object({
    ...proposalIdentityShape,
    binding: experienceStrategyProposalBindingWireSchema.optional(),
    draft: experienceStrategyDraftWireSchema,
    result_strategy_id: z.string(),
    result_version_id: z.string(),
  })
  .strict();

export const experienceStrategyProposalResponseSchema = z
  .object({
    proposal: experienceStrategyProposalWireSchema,
    schema_version: z.literal(1),
  })
  .strict();

export const experienceStrategyProposalPartSchema = z
  .object({
    data: z
      .object({
        action: z.enum(["confirm", "adjust"]),
        base_version_id: z.string().max(160),
        draft: experienceStrategyDraftWireSchema,
        expected_scope_revision: revisionSchema,
        expected_strategy_revision: z.string().max(30),
        proposal_hash: proposalHashSchema,
        proposal_id: domainIdSchema,
        proposal_revision: positiveRevisionSchema,
        schema: z.literal("data-experience-strategy-proposal.v1"),
        scope_id: domainIdSchema,
        status: z.enum(["drafted", "awaiting_confirmation"]),
        strategy_id: z.string().max(160),
      })
      .strict(),
    type: z.literal("data-experience-strategy-proposal"),
  })
  .strict();

const proposalStatusWireSchema = z.object(proposalIdentityShape).strict();

export const experienceStrategyProposalStatusResponseSchema = z
  .object({
    proposal: proposalStatusWireSchema,
    replayed: z.boolean(),
    schema_version: z.literal(1),
  })
  .strict();

export const experienceStrategyMutationResponseSchema = z
  .object({
    replayed: z.boolean(),
    schema_version: z.literal(1),
    strategy: experienceStrategyVersionWireSchema,
  })
  .strict();

export const conversationTurnSeekResponseSchema = z
  .object({
    page: z
      .object({
        after_sequence: revisionSchema,
        anchor_sequence: positiveRevisionSchema,
        through_sequence: positiveRevisionSchema,
      })
      .strict(),
    schema_version: z.literal(1),
    turn: z
      .object({
        end_sequence: positiveRevisionSchema,
        start_sequence: positiveRevisionSchema,
        status: z.enum([
          "completed",
          "failed",
          "pending",
          "stopped",
          "streaming",
        ]),
        turn_id: z.string().min(1),
      })
      .strict(),
  })
  .strict();

export type ExperienceStrategyDimensionKey = z.infer<
  typeof experienceStrategyDimensionKeySchema
>;
export type ExperienceStrategyDraft = z.infer<
  typeof experienceStrategyDraftWireSchema
>;
export type ExperienceStrategyVersion = z.infer<
  typeof experienceStrategyVersionWireSchema
>;
export type ExperienceStrategyProposal = z.infer<
  typeof experienceStrategyProposalWireSchema
>;
export type ExperienceStrategyProposalPart = z.infer<
  typeof experienceStrategyProposalPartSchema
>;

export function projectExperienceStrategyProposalPart(value: unknown) {
  const result = experienceStrategyProposalPartSchema.safeParse(value);
  return result.success ? result.data : null;
}
